import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Head from '@/components/feature/Head';
import { siteStats, testimonials } from '@/mocks/comics';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const webPageSchema = {
    name: '关于我们 - 韩漫在线免费',
    description: '了解韩漫在线免费的品牌故事、核心优势和联系方式。我们致力于为中文用户提供最优质的韩国漫画在线阅读服务。',
    url: 'https://www.koreancomics.cn/about',
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const advantages = [
    {
      icon: 'ri-book-read-line',
      title: '海量资源',
      desc: '收录超过12,000部优质韩国漫画，涵盖全类型题材，每日更新不断。',
    },
    {
      icon: 'ri-smartphone-line',
      title: '下拉阅读',
      desc: '创新的下拉式阅读设计，像浏览网页一样自然流畅，完美适配手机和电脑。',
    },
    {
      icon: 'ri-translate',
      title: '专业翻译',
      desc: '资深翻译团队精心打磨，确保内容准确传达，阅读体验原汁原味。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全纯净',
      desc: '无恶意广告，无弹窗骚扰，为您提供纯净舒适的阅读环境。',
    },
    {
      icon: 'ri-flashlight-line',
      title: '极速更新',
      desc: '紧跟韩国原版进度，热门作品同步更新，让您第一时间看到最新章节。',
    },
    {
      icon: 'ri-user-heart-line',
      title: '用户至上',
      desc: '倾听每一位读者的声音，持续优化产品体验，打造最好的韩漫社区。',
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9orp92qs38sj6jc7eb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <Head
        title="关于我们 - 韩漫在线免费 | 韩国漫画网站"
        description="了解韩漫在线免费的品牌故事与发展历程。我们是一个专注于韩国漫画的中文在线阅读平台，拥有海量韩漫资源和850万活跃读者，提供下拉式阅读体验。"
        keywords="韩漫在线, 关于我们, 韩国漫画网站, 漫画平台, 韩漫阅读"
        canonical="https://www.koreancomics.cn/about"
      />

      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={webPageSchema} />

      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-16">
          <Breadcrumbs items={breadcrumbItems} />

          {/* Hero */}
          <section className="w-full py-16 md:py-24 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">
                关于韩漫在线
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                我们是一个专注于<strong>韩国漫画</strong>的中文在线阅读平台，致力于为华语读者提供最优质的<strong>韩漫阅读</strong>体验。上线以来，已服务超过{siteStats.totalReaders.toLocaleString()}万读者。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full py-10 bg-white">
            <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center p-5 rounded-xl bg-background-50">
                  <p className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{siteStats.totalComics.toLocaleString()}+</p>
                  <p className="text-foreground-500 text-sm mt-1">漫画作品</p>
                </div>
                <div className="text-center p-5 rounded-xl bg-background-50">
                  <p className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{siteStats.totalReaders.toLocaleString()}+</p>
                  <p className="text-foreground-500 text-sm mt-1">活跃读者</p>
                </div>
                <div className="text-center p-5 rounded-xl bg-background-50">
                  <p className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{siteStats.dailyUpdates}+</p>
                  <p className="text-foreground-500 text-sm mt-1">每日更新</p>
                </div>
                <div className="text-center p-5 rounded-xl bg-background-50">
                  <p className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">{siteStats.avgRating}</p>
                  <p className="text-foreground-500 text-sm mt-1">平均评分</p>
                </div>
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
                    我们的故事
                  </h2>
                  <div className="space-y-4 text-foreground-600 text-sm leading-relaxed">
                    <p>
                      韩漫在线诞生于一群漫画爱好者的共同梦想。我们发现，尽管<strong>韩国漫画</strong>在全球范围内广受欢迎，但中文读者在寻找高质量的<strong>韩漫</strong>阅读平台时，经常面临资源分散、翻译质量参差不齐、阅读体验不佳等问题。
                    </p>
                    <p>
                      于是，我们决定亲手打造一个真正为中文读者设计的<strong>韩漫平台</strong>。从最初几十部漫画的小网站起步，到如今收录超过12,000部作品的综合性平台，我们始终坚守初心：让每一位喜欢韩国漫画的读者，都能在这里找到心中的热爱。
                    </p>
                    <p>
                      我们采用创新的<strong>下拉式阅读设计</strong>，打破了传统翻页模式的束缚，让阅读变得像浏览网页一样自然流畅。同时，我们组建了专业的翻译和编辑团队，确保每一部作品都能以最佳状态呈现给读者。
                    </p>
                    <p>
                      如今，韩漫在线已成长为中国领先的<strong>韩国漫画阅读平台</strong>之一，每天有数以万计的读者在这里享受漫画带来的快乐。未来，我们将继续前行，为读者带来更多优质内容和更好的阅读体验。
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <div className="rounded-xl overflow-hidden aspect-[4/3]">
                    <img
                      src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20style%20cozy%20reading%20room%20scene%2C%20warm%20lighting%20with%20bookshelves%20filled%20with%20comics%2C%20comfortable%20reading%20space%20with%20plants%20and%20soft%20textiles%2C%20inviting%20atmosphere%2C%20rich%20warm%20amber%20and%20cream%20tones%2C%20illustration%20style&width=800&height=600&seq=about-story-2026&orientation=landscape"
                      alt="韩漫在线 - 品牌故事"
                      title="韩漫在线 - 我们的故事"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Advantages */}
          <section className="w-full py-14 md:py-20 bg-white">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  为什么选择我们
                </h2>
                <p className="text-foreground-500 text-sm mt-2">六大核心优势，打造极致阅读体验</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {advantages.map((adv, i) => (
                  <div key={i} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                      <i className={`${adv.icon} text-xl text-primary-500`}></i>
                    </div>
                    <h3 className="text-foreground-900 font-semibold text-sm mb-2">{adv.title}</h3>
                    <p className="text-foreground-500 text-xs leading-relaxed">{adv.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h4 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  <a href="#">用户口碑</a>
                </h4>
                <p className="text-foreground-500 text-sm mt-2">听听读者们怎么说</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-5 rounded-xl bg-white border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(t.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-4">"{t.content}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-foreground-800 font-medium text-sm">{t.name}</p>
                        <p className="text-foreground-400 text-xs">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full py-14 md:py-20 bg-white">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
                    联系我们
                  </h2>
                  <p className="text-foreground-500 text-sm mb-8 leading-relaxed">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们会在第一时间回复您。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-foreground-800 font-medium text-sm">电子邮件</p>
                        <p className="text-foreground-500 text-xs">contact@koreancomics.cn</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-wechat-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-foreground-800 font-medium text-sm">微信公众号</p>
                        <p className="text-foreground-500 text-xs">搜索"韩漫在线"关注我们</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-phone-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-foreground-800 font-medium text-sm">客服热线</p>
                        <p className="text-foreground-500 text-xs">400-888-8888（工作日 9:00-18:00）</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-foreground-800 font-medium text-sm">公司地址</p>
                        <p className="text-foreground-500 text-xs">中国北京市朝阳区漫画大厦 1008室</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-xl p-6">
                  <h3 className="text-foreground-900 font-semibold text-base mb-4">发送消息</h3>
                  <form
                    id="contact-form"
                    data-readdy-form=""
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-foreground-700 text-sm mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-foreground-700 text-sm mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-foreground-700 text-sm mb-1.5">主题</label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-foreground-700 text-sm mb-1.5">留言</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <div className="form-honeypot" aria-hidden="true">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-green-600 text-sm text-center">消息已成功发送，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-red-500 text-sm text-center">{formError || '提交失败，请稍后重试'}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}